/**
 * Service layer — Feature Roadmap & Bug Tracker.
 *
 * Owns the tracker_entries table (Supabase) behind the Clerk-authed /api/tracker endpoint, plus the
 * /api/tracker-prompt generator. Components never call the API directly — they go through here.
 *
 * Entry shape: { id, ref, title, description, type, codebase, status, priority, images:[{url,
 * storage_path, name}], meta, created_at, updated_at, completed_at }.
 */

export const TRACKER_TYPES    = ['Bug', 'Feature Request', 'Suggestion', 'Migration']
// Backlog is the first landing place (items queue before Planning). Order = Kanban column order.
export const TRACKER_STATUSES = ['Backlog', 'Planning', 'In Progress', 'Complete']
export const TRACKER_CODEBASES = ['AlderCortex', 'Node Tree Viewer', 'QC Markup Tool']

/**
 * Get a Clerk token, waiting briefly for the getter to exist. The tracker workspace can mount and
 * fetch on a refresh-restore BEFORE OutlineView installs window.__clerkGetToken — without this wait
 * that first request goes out unauthenticated and 401s ("Authentication required"), leaving the list
 * empty until the user triggers another call. Mirrors services/notifications.js.
 */
async function token() {
  for (let i = 0; i < 25; i++) {
    if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* retry */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}

async function post(payload) {
  const headers = { 'Content-Type': 'application/json' }
  const t = await token(); if (t) headers['Authorization'] = 'Bearer ' + t
  const res = await fetch('/api/tracker', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/tracker' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

export async function listEntries()        { return (await post({ action: 'list' })).entries || [] }
export async function createEntry(fields)  { return (await post({ action: 'create', ...fields })).entry }
export async function updateEntry(id, fields) { return (await post({ action: 'update', id, ...fields })).entry }
export async function reorderEntries(order)   { return (await post({ action: 'reorder', order })).entries || [] }
export async function deleteEntry(id)         { return (await post({ action: 'delete', id })).entries || [] }

/**
 * Upload a context image to Storage (signed-URL flow) and return its meta { url, storage_path, name }
 * for inclusion in an entry's images array.
 */
export async function uploadTrackerImage(file) {
  const { signedUrl, token: upToken, storagePath, publicUrl } = await post({ action: 'getUploadUrl', filename: file.name || 'image.png' })
  const put = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true', ...(upToken ? { Authorization: 'Bearer ' + upToken } : {}) },
    body: file,
  })
  if (!put.ok) throw new Error('Image upload failed (' + put.status + ')')
  return { url: publicUrl, storage_path: storagePath, name: file.name || 'image.png' }
}

/**
 * Generate a Claude Code prompt for an entry. The server expands via the Anthropic API and falls back
 * to a deterministic template. Returns { prompt, fallback, note? }.
 */
export async function generatePrompt(entry) {
  const headers = { 'Content-Type': 'application/json' }
  const t = await token(); if (t) headers['Authorization'] = 'Bearer ' + t
  const res = await fetch('/api/tracker-prompt', { method: 'POST', headers, body: JSON.stringify({ entry }) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/tracker-prompt' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}
